import { Col, FormControl, FormSelect } from "react-bootstrap";
import React, {useCallback, useEffect, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import FishProtectionDTO from "../../dtos/FishProtectionDTO";
import moment from 'moment-timezone';
import { getOrganisation } from "../../service/LoadStorageData";

export function ProtectionTimePeriodComponent({path, idx, initialValue}: {
    path?: string,
    idx: number,
    initialValue?: FishProtectionDTO,
}) {
    const fullPath = `${path ? `${path}.` : ""}${idx}`;
    const currentYear = new Date().getFullYear();
    const firstDayOfYear = new Date(Date.UTC(currentYear, 0, 1, 0, 0, 0, 0));
    const lastSecond = new Date(Date.UTC(currentYear, 11, 31, 0, 0, 0));

    const formMethods = useFormContext();
    const { register, setValue } = formMethods;
    const [readOnly, setReadOnly] = useState<boolean>(true);
    const [required, setRequired] = useState<boolean>(false);
    const [displayFrom, setDisplayFrom] = useState<string>("");
    const [displayUntil, setDisplayUntil] = useState<string>("");

    const toDateInputValue = useCallback((date: Date) => {
        const org = getOrganisation();
        const tz = (org && (org as any).timeZone) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        return moment(date).tz(tz).format('YYYY-MM-DD');
    }, []);

    const normalizeDateInput = useCallback((value?: string | Date | null) => {
        if (!value) {
            return "";
        }
        const org = getOrganisation();
        const tz = (org && (org as any).timeZone) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        if (value instanceof Date) {
            return toDateInputValue(value);
        }
        if (typeof value === "string") {
            try {
                // if string contains a time or offset, parse respecting that
                if (value.includes('T')) {
                    const m = /[zZ]$|[-+]\d{2}:\d{2}$/.test(value) ? moment.parseZone(value).tz(tz) : moment.tz(value, tz);
                    if (m.isValid()) return m.format('YYYY-MM-DD');
                } else {
                    const m = moment.tz(value, 'YYYY-MM-DD', tz);
                    if (m.isValid()) return m.format('YYYY-MM-DD');
                }
            } catch (e) {
                // fallback
            }
            return value.split("T")[0];
        }
        return "";
    }, [toDateInputValue]);

    const isoToDisplay = useCallback((iso?: string | Date | null) => {
        if (!iso) return "";
        const org = getOrganisation();
        const tz = (org && (org as any).timeZone) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        try {
            if (iso instanceof Date) {
                return moment(iso).tz(tz).format('DD.MM.YYYY');
            }
            if (typeof iso === 'string') {
                // if string has offset/Z, parse preserving offset then convert to org tz
                if (/[zZ]$|[-+]\d{2}:\d{2}$/.test(iso)) {
                    const m = moment.parseZone(iso).tz(tz);
                    if (m.isValid()) return m.format('DD.MM.YYYY');
                }
                const m = moment.tz(iso, tz);
                if (m.isValid()) return m.format('DD.MM.YYYY');

                const dateStr = iso.includes('T') ? iso.split('T')[0] : iso;
                const parts = dateStr.split("-");
                if (parts.length !== 3) return dateStr;
                return `${parts[2]}.${parts[1]}.${parts[0]}`;
            }
        } catch (e) {
            // fall through
        }
        return "";
    }, []);

    const displayToIso = useCallback((display?: string) => {
        if (!display) return "";
        const parts = display.split(".").map(p => p.trim());
        if (parts.length !== 3) return display;
        const [d, m, y] = parts;
        // basic validation
        if (y.length !== 4) return display;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }, []);

    const setDate = useCallback((path: string, date: Date) => {
        setValue(path, toDateInputValue(date));
    }, [setValue, toDateInputValue])

    useEffect(() => {
        if (initialValue) {

            if (initialValue.hasProtectionTime) {
                setRequired(!initialValue.wholeYearProtected);
                setReadOnly(initialValue.wholeYearProtected);
            }

            // prefer `fromIso`/`untilIso` when present (they are UTC); fall back to legacy `from`/`until`
            const incomingFrom = initialValue.fromIso ?? (initialValue as any).from;
            const incomingUntil = initialValue.untilIso ?? (initialValue as any).until;
            if (incomingFrom && incomingUntil) {
                setValue(
                    `${fullPath}.fromIso`,
                    normalizeDateInput(incomingFrom)
                );
                setValue(
                    `${fullPath}.untilIso`,
                    normalizeDateInput(incomingUntil)
                );
                setDisplayFrom(isoToDisplay(incomingFrom as string));
                setDisplayUntil(isoToDisplay(incomingUntil as string));
            }

            // initialize protectionType / flags in the form so UI matches data
            if (typeof initialValue.protectionType !== 'undefined') {
                setValue(`${fullPath}.protectionType`, String(initialValue.protectionType));
            }
            setValue(`${fullPath}.hasProtectionTime`, !!initialValue.hasProtectionTime);
            setValue(`${fullPath}.wholeYearProtected`, !!initialValue.wholeYearProtected);
        }
    }, [initialValue, fullPath, setValue, setDate, normalizeDateInput, isoToDisplay, idx]);

    const updateProtectionType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const val = event.target.value;
        setValue(`${fullPath}.hasProtectionTime`, val !== "0");
        setValue(`${fullPath}.wholeYearProtected`, val === "1");
        setRequired(val === "2");
        setReadOnly(val !== "2");

        if (val === "0") {
            setValue(`${fullPath}.fromIso`, "");
            setValue(`${fullPath}.untilIso`, "");
            setDisplayFrom("");
            setDisplayUntil("");
        }

        if (val === "1") {
            setDate(`${fullPath}.fromIso`, firstDayOfYear);
            setDate(`${fullPath}.untilIso`, lastSecond);
            setDisplayFrom(isoToDisplay(toDateInputValue(firstDayOfYear)));
            setDisplayUntil(isoToDisplay(toDateInputValue(lastSecond)));
        }
    };

    return <>
        <Col xs={3}>
            <FormSelect
                defaultValue={"0"}
                {...register(`${fullPath}.protectionType`, {
                    required: true,
                    valueAsNumber: true,
                    onChange: updateProtectionType
                })}
            >
                <option value={"0"}>Keine Schonzeit</option>
                <option value={"1"}>Ganzjährig geschont</option>
                <option value={"2"}>Schonzeit pflegen</option>
            </FormSelect>
        </Col>
        <Col xs={2}>
            <Controller render={({ field }) => (<FormControl type={'hidden'} {... field} />)} name={`${fullPath}.wholeYearProtected`} />
            {/* hidden ISO fields for submission/storage */}
            <FormControl type={'hidden'} {...register(`${fullPath}.fromIso`, { required: required })} />
            <FormControl type={'hidden'} {...register(`${fullPath}.untilIso`, { required: required })} />

            {/* visible fields in dd.mm.yyyy for users */}
            <FormControl
                type={"text"}
                placeholder={"dd.mm.yyyy"}
                disabled={readOnly}
                value={displayFrom}
                onChange={(e) => {
                    const v = e.target.value;
                    setDisplayFrom(v);
                    setValue(`${fullPath}.fromIso`, displayToIso(v));
                }}
            />
            <FormControl
                type={"text"}
                placeholder={"dd.mm.yyyy"}
                disabled={readOnly}
                value={displayUntil}
                onChange={(e) => {
                    const v = e.target.value;
                    setDisplayUntil(v);
                    setValue(`${fullPath}.untilIso`, displayToIso(v));
                }}
            />
        </Col>
    </>;
}
